import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dayKey, formatWeekdayDay } from "../format.js";
import { Pagination } from "../Pagination.js";
import { getFamilyPracticeResources, type FamilyPracticeResource } from "../resources.js";
import { useAsync } from "../useAsync.js";
import { trackUsage } from "../analytics/usage.js";

function ResourceRows({ items }: { items: FamilyPracticeResource[] }) {
  return <ul className="practice-resource-list">
    {items.map((resource) => <li className="practice-resource-row" key={resource.id}>
      <span className="practice-resource-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          {resource.kind === "groove" ? <><ellipse cx="12" cy="8" rx="8" ry="3" /><path d="M4 8v8c0 4 16 4 16 0V8M4 11l4 6 4-6 4 6 4-6" /></>
            : resource.kind === "link" ? <><path d="M14 3h7v7M21 3l-11 11M10 3H4v17h17v-6" /></>
              : <><path d="M14 2H5v20h14V7l-5-5ZM14 2v5h5M8 12h8M8 16h8" /></>}
        </svg>
      </span>
      <div>
        <strong className="practice-resource-name">{resource.title}</strong>
        {resource.description && <p className="small muted">{resource.description}</p>}
        <div className="practice-resource-meta small">
          <span className="muted">{resource.presentationKind === "pdf" ? "PDF" : resource.kind === "link" ? "Web link" : resource.presentationKind.charAt(0).toUpperCase() + resource.presentationKind.slice(1)}</span>
          {resource.kind === "groove"
            ? <Link to={resource.href} aria-label={`Open and play ${resource.title}`} onClick={() => trackUsage("practice_resource_open")}>Open and play</Link>
            : <a href={resource.href} target="_blank" rel="noopener noreferrer" aria-label={`Open ${resource.title}`} onClick={() => trackUsage("practice_resource_open")}>Open ↗</a>}
        </div>
      </div>
    </li>)}
  </ul>;
}

export function FamilyResources({ studentId }: { studentId: string }) {
  return <StudentResources key={studentId} studentId={studentId} />;
}

function StudentResources({ studentId }: { studentId: string }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [kind, setKind] = useState<"all" | "files" | "grooves">("all");
  useEffect(() => {
    const nextSearch = search.trim();
    if (nextSearch === debouncedSearch) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(nextSearch);
      setPage(1);
    }, 200);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);
  const loadKey = JSON.stringify([studentId, page, debouncedSearch, kind]);
  const resources = useAsync(async () => ({
    key: loadKey,
    filtering: debouncedSearch !== "" || kind !== "all" || page > 1,
    result: await getFamilyPracticeResources(studentId, page, debouncedSearch, kind),
  }), [loadKey]);
  // Retain this student's last page while refreshing. The keyed student
  // boundary and response identity still prevent showing another family's links.
  const previous = resources.data?.result.studentId === studentId ? resources.data : null;
  const data = previous?.result ?? null;
  const current = previous?.key === loadKey;
  const refreshing = !resources.error && (resources.loading || !current);
  const groups = new Map<string, FamilyPracticeResource[]>();
  for (const item of data?.items ?? []) {
    const group = groups.get(item.groupId) ?? [];
    group.push(item);
    groups.set(item.groupId, group);
  }
  const grouped = [...groups.entries()];
  const filtering = previous?.filtering ?? false;
  const showGroups = (entries: typeof grouped) => entries.map(([id, items]) => <div key={id} className="practice-resource-group">
    <h3 className="small muted">{items[0]!.groupLabel} · {formatWeekdayDay(dayKey(items[0]!.sharedAt))}</h3>
    <ResourceRows items={items} />
  </div>);

  return <section aria-labelledby="family-resources-heading" className="practice-resources" aria-busy={refreshing}>
    <div className="practice-section-heading">
      <h2 id="family-resources-heading">Relevant resources</h2>
      {data && <span className="small muted" role="status">{refreshing && !resources.error ? "Updating…" : data.sharedTotal}</span>}
    </div>
    <label className="practice-resource-search">
      <span className="visually-hidden">Search shared resources</span>
      <input type="search" value={search} placeholder="Search resources…" maxLength={200}
        onChange={(event) => setSearch(event.target.value)} />
    </label>
    <div className="practice-resource-filters" role="group" aria-label="Resource types">
      {([['all', 'All'], ['files', 'Files & links'], ['grooves', 'Grooves']] as const).map(([value, label]) =>
        <button type="button" key={value} className="quiet small" aria-pressed={kind === value}
          onClick={() => { setKind(value); setPage(1); }}>{label}</button>)}
    </div>
    {resources.error && <div className="error small" role="alert">Resources could not be loaded: {resources.error}
      <button type="button" className="link small" onClick={resources.reload}>Try again</button>
    </div>}
    {!data && refreshing && !resources.error && <p className="small muted" role="status">Loading resources…</p>}
    {data && data.items.length === 0 && !refreshing && !resources.error && <p className="small muted practice-resource-empty">
      {data.sharedTotal === 0 ? "No resources have been shared yet." : "No resources match these filters."}
    </p>}
    {filtering ? showGroups(grouped) : <>
      {showGroups(grouped.slice(0, 1))}
      {grouped.length > 1 && <details className="practice-earlier-resources">
        <summary>Earlier shared resources</summary>
        {showGroups(grouped.slice(1))}
      </details>}
    </>}
    {data && <Pagination page={data.page} pages={data.pages} ariaLabel="Resources pagination" disabled={!current || resources.loading} onPageChange={setPage} />}
  </section>;
}
